import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import CountUp from 'react-countup'
import dayjs from 'dayjs'
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  RadialLinearScale,
  Filler,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import { Bar, Bubble, Doughnut, Line, Radar } from 'react-chartjs-2'
import {
  FiUsers,
  FiDollarSign,
  FiCalendar,
  FiTrendingUp,
  FiActivity,
  FiRefreshCw,
  FiClock,
  FiUserCheck
} from 'react-icons/fi'
import AuthContext from 'context/AuthContext'
import ODateRangePicker from 'components/shared/datePicker/ODateRangePicker'
import { apiGet } from 'utils/apiFetch'
import apiPath from 'utils/apiPath'

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  RadialLinearScale,
  Filler,
  Title,
  Tooltip,
  Legend
)

const CHART_COLORS = ['#3E94AC', '#2B3467', '#63C3EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899']

const emptyData = {
  hr: {},
  payroll: {},
  staffing: {},
  revenue: {},
  workforce: {}
}

const toYmd = (value) => {
  if (value === undefined || value === null || value === '') return ''
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return dayjs(value).format('YYYY-MM-DD')
  }
  const raw = String(value).trim()
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`
  const mdy = raw.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/)
  if (mdy) {
    const month = Number(mdy[1])
    const day = Number(mdy[2])
    const year = Number(mdy[3])
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    }
  }
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : ''
}

const clampToCurrentYear = (ymd) => {
  if (!ymd) return ''
  const year = Number(String(ymd).slice(0, 4))
  const currentYear = dayjs().year()
  if (!year || year > currentYear) return ''
  return ymd
}

const toPickerDate = (value) => {
  const ymd = toYmd(value)
  if (!ymd) return ''
  const [year, month, day] = ymd.split('-').map(Number)
  return new Date(year, month - 1, day, 12, 0, 0)
}

const pickBiPayload = (rawResults) => {
  if (!rawResults || typeof rawResults !== 'object') return {}
  if (rawResults.workforce || rawResults.hr) return rawResults
  if (rawResults.results && (rawResults.results.workforce || rawResults.results.hr)) {
    return rawResults.results
  }
  return rawResults
}

const toAttendanceRow = (row = {}) => ({
  employeeName: row.employeeName || row.name || row.employee?.name || 'Unknown',
  employeeId: row.employeeId || row.employee?.employeeId || 'N/A',
  companyId: row.companyId ? String(row.companyId) : '',
  companyName: row.companyName || 'Unknown',
  daysWorked: Number(row.daysWorked || 0),
  absentDays: Number(row.absentDays ?? row.absent ?? 0),
  lateDays: Number(row.lateDays ?? row.late ?? 0),
  earlyDepartures: Number(row.earlyDepartures || 0),
  missedClockOuts: Number(row.missedClockOuts || 0),
  onTimeRate: Number(row.onTimeRate || 0)
})

const pickAttendanceRows = (workforce = {}) => {
  let raw = workforce.attendanceRows
  if (!Array.isArray(raw) && Array.isArray(workforce.results)) raw = workforce.results
  if (raw && !Array.isArray(raw) && Array.isArray(raw.results)) raw = raw.results
  if (!Array.isArray(raw)) return []
  return raw.map(toAttendanceRow)
}

const pickAttendanceByDay = (workforce = {}) => {
  const raw = workforce.attendanceByDay
  if (!Array.isArray(raw)) return []
  return raw
    .map((row = {}) => ({
      date: toYmd(row.date) || String(row.date || '').slice(0, 10),
      companyId: row.companyId ? String(row.companyId) : '',
      companyName: row.companyName || 'Unknown',
      present: Number(row.present || 0),
      absent: Number(row.absent || 0)
    }))
    .filter((row) => row.date)
}

const WEEKDAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']

const startOfWeekMonday = (value) => {
  const parsed = dayjs(value)
  if (!parsed.isValid()) return dayjs().startOf('day')
  const day = parsed.day()
  const diff = day === 0 ? 6 : day - 1
  return parsed.subtract(diff, 'day').startOf('day')
}

const clampDateToMonth = (ymd, monthValue) => {
  const fallbackDay = Number(String(ymd || '').slice(8, 10)) || 1
  const [year, month] = String(monthValue || '').split('-').map(Number)
  if (!year || !month) return ymd
  const daysInMonth = dayjs(`${year}-${String(month).padStart(2, '0')}-01`).daysInMonth()
  const day = Math.min(Math.max(fallbackDay, 1), daysInMonth)
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

const formatDayTitle = (ymd) => {
  const parsed = dayjs(ymd)
  return parsed.isValid() ? parsed.format('dddd, DD MMM YYYY') : ymd
}

const toHoursRow = (row = {}) => {
  const daysPresent = Number(row.daysPresent ?? row.daysWorked ?? 0)
  const totalRegularHours = Number(row.totalRegularHours ?? row.regularHours ?? row.regular ?? 0)
  const totalOvertimeHours = Number(row.totalOvertimeHours ?? row.overtimeHours ?? row.overtime ?? 0)
  const avgHoursPerDay = daysPresent
    ? Number((((totalRegularHours + totalOvertimeHours) / daysPresent)).toFixed(1))
    : Number(row.avgHoursPerDay || 0)
  return {
    employeeName: row.employeeName || row.name || row.employee?.name || 'Unknown',
    employeeId: row.employeeId || row.employee?.employeeId || 'N/A',
    daysPresent,
    totalRegularHours,
    totalOvertimeHours,
    totalBreakHours: Number(row.totalBreakHours ?? row.breakHours ?? 0),
    avgHoursPerDay
  }
}

const pickHoursRows = (workforce = {}) => {
  let raw = workforce.hoursRows
  if (!Array.isArray(raw) && Array.isArray(workforce.hours)) raw = workforce.hours
  if (raw && !Array.isArray(raw) && Array.isArray(raw.results)) raw = raw.results
  if (!Array.isArray(raw)) return []
  return raw.map(toHoursRow)
}

const formatMoney = (value) =>
  Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const labelize = (value) =>
  String(value || 'unknown')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())

function KpiCard({ title, value, suffix, prefix, color, icon: Icon, to, onClick, subtitle, isMoney, textValue, desc="" }) {
  const colorMap = {
    blue: 'bg-blue-50 border-blue-100 text-blue-700',
    indigo: 'bg-indigo-50 border-indigo-100 text-indigo-700',
    green: 'bg-emerald-50 border-emerald-100 text-emerald-700',
    amber: 'bg-amber-50 border-amber-100 text-amber-700',
    purple: 'bg-purple-50 border-purple-100 text-purple-700',
    pink: 'bg-pink-50 border-pink-100 text-pink-700'
  }
  const numericValue = Number(value || 0)
  const content = (
    <div className={`h-full rounded-lg border p-4 hover:shadow-md transition duration-200 ${colorMap[color] || colorMap.blue}`} title={desc}>
      <div className='flex items-start justify-between gap-2'>
        <p className='text-xs font-medium uppercase tracking-wide opacity-80'>{title}</p>
        {Icon ? <Icon className='text-lg opacity-70' /> : null}
      </div>
      {textValue ? (
        <p className='mt-2 text-lg font-semibold text-slate-500 italic'>{textValue}</p>
      ) : (
        <p className='mt-2 text-2xl font-bold text-slate-900'>
          {isMoney ? `$${formatMoney(numericValue)}` : (
            <>
              {prefix || ''}
              <CountUp end={numericValue} duration={0.6} separator=',' decimals={Number.isInteger(numericValue) ? 0 : 1} />
              {suffix || ''}
            </>
          )}
        </p>
      )}
      {subtitle ? <p className='mt-1 text-xs text-slate-500'>{subtitle}</p> : null}
    </div>
  )

  if (to) {
    return (
      <Link to={to} onClick={onClick} className='block h-full'>
        {content}
      </Link>
    )
  }
  return content
}

function ChartCard({ title, children }) {
  return (
    <div className='bg-white border border-[#E1E6EE] rounded-lg p-4 shadow-sm'>
      <h3 className='text-sm font-semibold text-gray-700 mb-3'>{title}</h3>
      <div className='h-64 w-full'>{children}</div>
    </div>
  )
}

function AttendanceOverviewChart({ rows = [], dailyRows = [] }) {
  const [companyKey, setCompanyKey] = useState('all')
  const [selectedMonth, setSelectedMonth] = useState('')
  const [selectedDate, setSelectedDate] = useState('')

  const companies = useMemo(() => {
    const grouped = new Map()
    const addCompany = (id, name) => {
      const key = id || name || 'unknown'
      if (!grouped.has(key)) grouped.set(key, { id: key, name: name || 'Unknown' })
    }
    dailyRows.forEach((row) => addCompany(row.companyId, row.companyName))
    rows.forEach((row) => addCompany(row.companyId, row.companyName))
    return Array.from(grouped.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [dailyRows, rows])

  const latestDataDate = useMemo(() => {
    const dates = dailyRows.map((row) => row.date).filter(Boolean).sort()
    return dates[dates.length - 1] || dayjs().format('YYYY-MM-DD')
  }, [dailyRows])

  useEffect(() => {
    if (companyKey !== 'all' && !companies.some((company) => company.id === companyKey)) {
      setCompanyKey('all')
    }
  }, [companies, companyKey])

  useEffect(() => {
    if (selectedDate) return
    setSelectedDate(latestDataDate)
    setSelectedMonth(String(latestDataDate).slice(0, 7))
  }, [latestDataDate, selectedDate])

  const selected = companyKey === 'all' ? null : companies.find((company) => company.id === companyKey)
  const activeDate = selectedDate || latestDataDate
  const activeMonth = selectedMonth || String(activeDate).slice(0, 7)
  const weekStart = startOfWeekMonday(activeDate)
  const weekEnd = weekStart.add(4, 'day')

  const companyDailyRows = useMemo(() => (
    selected
      ? dailyRows.filter((row) => row.companyId === selected.id || row.companyName === selected.name)
      : dailyRows
  ), [dailyRows, selected])

  const monthHasData = useMemo(() => (
    companyDailyRows.some((row) => {
      const inMonth = String(row.date || '').startsWith(activeMonth)
      return inMonth && (Number(row.present || 0) > 0 || Number(row.absent || 0) > 0)
    })
  ), [companyDailyRows, activeMonth])

  const handleMonthChange = (event) => {
    const monthValue = event.target.value
    if (!monthValue) return
    const nextDate = clampDateToMonth(selectedDate || latestDataDate, monthValue)
    setSelectedMonth(monthValue)
    setSelectedDate(nextDate)
  }

  const handleDateChange = (event) => {
    const nextDate = toYmd(event.target.value)
    if (!nextDate) return
    setSelectedDate(nextDate)
    setSelectedMonth(nextDate.slice(0, 7))
  }

  const chartItems = useMemo(() => {
    const monday = startOfWeekMonday(activeDate)
    const byDate = new Map()
    companyDailyRows.forEach((row) => {
      const current = byDate.get(row.date) || { date: row.date, present: 0, absent: 0 }
      current.present += Number(row.present || 0)
      current.absent += Number(row.absent || 0)
      byDate.set(row.date, current)
    })
    return WEEKDAY_LABELS.map((label, index) => {
      const date = monday.add(index, 'day').format('YYYY-MM-DD')
      const current = byDate.get(date) || { present: 0, absent: 0 }
      return {
        date,
        label,
        present: Number(current.present || 0),
        absent: Number(current.absent || 0)
      }
    })
  }, [companyDailyRows, activeDate])

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { usePointStyle: true, padding: 16 } },
      tooltip: {
        callbacks: {
          title: (items) => formatDayTitle(chartItems[items[0]?.dataIndex]?.date || items[0]?.label),
          label: (context) => `${context.dataset.label}: ${context.parsed.y}`
        }
      }
    },
    scales: {
      x: {
        stacked: false,
        grid: { display: false },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          autoSkip: false,
          font: { size: 11 }
        }
      },
      y: {
        beginAtZero: true,
        stacked: false,
        ticks: {
          stepSize: 5,
          precision: 0,
          font: { size: 10 }
        }
      }
    }
  }

  const controlClass = 'text-xs border border-[#E1E6EE] rounded-lg px-2 py-1 bg-white text-slate-700'

  return (
    <div className='bg-white border border-[#E1E6EE] rounded-lg p-4 shadow-sm'>
      <div className='flex flex-wrap items-start justify-between gap-2 mb-3'>
        <div>
          <h3 className='text-sm font-semibold text-gray-700'>Attendance Overview</h3>
          <p className='text-xs text-slate-500 mt-0.5'>
            {selected
              ? `${selected.name} · weekday present vs absent`
              : `${companies.length} companies · weekday present vs absent`}
          </p>
          {monthHasData ? (
            <p className='text-xs text-slate-400 mt-0.5'>
              {weekStart.format('DD MMM')} – {weekEnd.format('DD MMM YYYY')}
            </p>
          ) : null}
        </div>
        <div className='flex flex-wrap items-center justify-end gap-2'>
          <label className='flex items-center gap-1 text-xs text-slate-500'>
            Month
            <input
              type='month'
              value={selectedMonth}
              onChange={handleMonthChange}
              className={controlClass}
            />
          </label>
          <label className='flex items-center gap-1 text-xs text-slate-500'>
            Date
            <input
              type='date'
              value={selectedDate}
              onChange={handleDateChange}
              className={controlClass}
            />
          </label>
          {selected ? (
            <button
              type='button'
              onClick={() => setCompanyKey('all')}
              className='text-xs px-2 py-1 rounded border border-[#E1E6EE] text-slate-600 hover:bg-slate-50'
            >
              All companies
            </button>
          ) : null}
          <select
            value={companyKey}
            onChange={(event) => setCompanyKey(event.target.value)}
            className={`${controlClass} max-w-[220px]`}
          >
            <option value='all'>All companies ({companies.length})</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      {monthHasData ? (
        <div style={{ height: 256, width: '100%' }}>
          <Bar
            options={options}
            data={{
              labels: chartItems.map((item) => item.label),
              datasets: [
                {
                  label: 'Present',
                  data: chartItems.map((item) => item.present || 0),
                  backgroundColor: 'rgba(62, 84, 172, 0.7)',
                  borderColor: '#3E54AC',
                  borderWidth: 1,
                  borderRadius: 3,
                  maxBarThickness: 36
                },
                {
                  label: 'Absent',
                  data: chartItems.map((item) => item.absent || 0),
                  backgroundColor: 'rgba(239, 68, 68, 0.7)',
                  borderColor: '#EF4444',
                  borderWidth: 1,
                  borderRadius: 3,
                  maxBarThickness: 36
                }
              ]
            }}
          />
        </div>
      ) : (
        <div className='flex items-center justify-center h-64 text-gray-400 text-sm'>
          No data found on this month
        </div>
      )}
    </div>
  )
}

function ReportKpi({ label, value, color }) {
  const colorMap = {
    blue: 'text-[#2563EB] bg-blue-50 border-blue-100',
    green: 'text-green-600 bg-green-50 border-green-100',
    orange: 'text-orange-500 bg-orange-50 border-orange-100',
    red: 'text-red-500 bg-red-50 border-red-100',
    purple: 'text-purple-600 bg-purple-50 border-purple-100'
  }
  return (
    <div className={`rounded-lg border p-4 ${colorMap[color] || colorMap.blue}`}>
      <p className='text-xs text-gray-500 mb-1'>{label}</p>
      <p className={`text-2xl font-bold ${colorMap[color]?.split(' ')[0]}`}>{value}</p>
    </div>
  )
}

function BusinessIntelligence({
  endpoint,
  embedded = false,
  hidePageTitle = false,
  kpiLinks = {},
  totalEarningTillDate,
  totalRevenue: totalRevenueProp,
  startDate: externalStartDate,
  endDate: externalEndDate
}) {
  const { t } = useTranslation()
  const { logoutUser, updatePageName, user } = useContext(AuthContext)
  const apiEndpoint = endpoint || apiPath.getAdminBusinessIntelligence
  const [data, setData] = useState(emptyData)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [isReset, setIsReset] = useState(false)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeDomain, setActiveDomain] = useState('hr')
  const inFlightRef = useRef(false)

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom', labels: { usePointStyle: true, padding: 16 } } },
    scales: { y: { beginAtZero: true } }
  }), [])

  const pieOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom', labels: { usePointStyle: true, padding: 16 } } }
  }), [])

  const fetchData = useCallback(async (silent = false) => {
    if (inFlightRef.current) return
    inFlightRef.current = true
    try {
      if (!silent) setLoading(true)
      const start = clampToCurrentYear(toYmd(startDate))
      const end = clampToCurrentYear(toYmd(endDate))
      const payload = {}
      if (start) payload.startDate = start
      if (end) payload.endDate = end
      const result = await apiGet(apiEndpoint, payload, {
        silent,
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
      })
      if (result?.data?.success) {
        setData({ ...emptyData, ...pickBiPayload(result.data.results) })
        setLastUpdated(result.data.results?.generatedAt || pickBiPayload(result.data.results)?.generatedAt || new Date().toISOString())
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 409) logoutUser()
    } finally {
      inFlightRef.current = false
      setLoading(false)
    }
  }, [startDate, endDate, logoutUser, apiEndpoint])

  useEffect(() => {
    if (!hidePageTitle && !embedded) {
      updatePageName(t('NAV_BI_DASHBOARD'))
    }
  }, [updatePageName, t, hidePageTitle, embedded])

  useEffect(() => {
    fetchData(false)
  }, [fetchData])

  // PREVIOUS: setInterval with no delay defaulted to 0ms and flooded
  // GET /v1/admin/business-intelligence while this page was open.
  // useEffect(() => {
  //   const timer = setInterval(() => fetchData(true))
  //   return () => clearInterval(timer)
  // }, [fetchData])

  useEffect(() => {
    if (!embedded) return
    setStartDate(clampToCurrentYear(toYmd(externalStartDate)))
    setEndDate(clampToCurrentYear(toYmd(externalEndDate)))
  }, [embedded, externalStartDate, externalEndDate])

  const handleDateChange = (start, end) => {
    setStartDate(clampToCurrentYear(toYmd(start)))
    setEndDate(clampToCurrentYear(toYmd(end)))
  }

  const handleReset = () => {
    setStartDate('')
    setEndDate('')
    setIsReset(true)
  }

  const doughnutFromGroups = (rows, valueField = 'count') => {
    const labels = (rows || []).map((row) => labelize(row._id))
    const values = (rows || []).map((row) => row[valueField] || row.count || 0)
    return {
      labels: labels.length ? labels : ['No data'],
      datasets: [{ data: values.length ? values : [1], backgroundColor: CHART_COLORS, borderWidth: 1 }]
    }
  }

  const barFromGroups = (rows, label) => ({
    labels: (rows || []).map((row) => labelize(row._id)),
    datasets: [{
      label,
      data: (rows || []).map((row) => row.count || row.totalAmount || 0),
      backgroundColor: 'rgba(62, 84, 172, 0.7)',
      borderColor: '#3E54AC',
      borderWidth: 1
    }]
  })

  const lineFromTrend = (rows, keys) => ({
    labels: (rows || []).map((row) => row._id || ''),
    datasets: keys.map((key, index) => ({
      label: key.label,
      data: (rows || []).map((row) => row[key.field] || 0),
      borderColor: CHART_COLORS[index],
      backgroundColor: `${CHART_COLORS[index]}33`,
      fill: true,
      tension: 0.3,
      pointRadius: 3
    }))
  })

  const isAdminRole = user?.role === 'admin' || user?.role === 'subadmin'
  const links = {
    employees: isAdminRole ? '/company-manager' : null,
    payroll: isAdminRole ? '/earning-managers' : null,
    ...kpiLinks
  }

  const hr = data.hr || {}
  const payroll = data.payroll || {}
  const staffing = data.staffing || {}
  const revenue = data.revenue || {}
  const workforce = data.workforce || {}
  const displayRevenue = Number(revenue.totalRevenue) || Number(totalEarningTillDate) || Number(totalRevenueProp) || 0

  const domains = [
    { key: 'hr', label: t('BI_HR'), icon: FiUsers },
    { key: 'payroll', label: t('BI_PAYROLL'), icon: FiDollarSign },
    { key: 'staffing', label: t('BI_STAFFING'), icon: FiCalendar },
    { key: 'revenue', label: t('BI_REVENUE'), icon: FiTrendingUp },
    { key: 'workforce', label: t('BI_WORKFORCE'), icon: FiClock }
  ]

  const show = (key) => activeDomain === 'all' || activeDomain === key
  const growthLabel = `${revenue.growth >= 0 ? '+' : ''}${revenue.growth || 0}% ${t('BI_VS_PREVIOUS')}`
  const attendanceRows = pickAttendanceRows(workforce)
  const attendanceByDay = pickAttendanceByDay(workforce)
  const overtimeRows = Array.isArray(workforce.overtimeRows) ? workforce.overtimeRows : []
  const hoursRows = pickHoursRows(workforce)

  const totalLate = attendanceRows.reduce((sum, row) => sum + (row.lateDays || 0), 0)
  const totalAbsent = attendanceRows.reduce((sum, row) => sum + (row.absentDays || 0), 0)
  const avgOnTimeRate = attendanceRows.length
    ? (attendanceRows.reduce((sum, row) => sum + (row.onTimeRate || 0), 0) / attendanceRows.length).toFixed(1)
    : '0.0'
  const totalRegularHours = hoursRows.reduce((sum, row) => sum + (row.totalRegularHours || 0), 0)
  const totalHoursOt = hoursRows.reduce((sum, row) => sum + (row.totalOvertimeHours || 0), 0)
  const totalDaysPresent = hoursRows.reduce((sum, row) => sum + (row.daysPresent || 0), 0)
  const avgHoursPerDay = totalDaysPresent
    ? ((totalRegularHours + totalHoursOt) / totalDaysPresent).toFixed(1)
    : '0.0'
  const totalOTHours = overtimeRows.reduce((sum, row) => sum + (row.totalOvertimeHours || 0), 0)
  const totalWorkingHours = totalRegularHours + totalHoursOt
  const totalWorkforce = hoursRows.length || workforce.totalWorkforce || 0
  const activeWorkforce = hoursRows.length
    ? hoursRows.filter((row) => (row.daysPresent || 0) > 0).length
    : workforce.activeWorkforce || 0
  const radarEmployees = attendanceRows.slice(0, 6)
  const attendanceRadarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { usePointStyle: true, boxWidth: 8, font: { size: 10 } }
      }
    },
    scales: {
      r: {
        beginAtZero: true,
        grid: { color: '#e5e7eb' },
        pointLabels: { font: { size: 10 } },
        ticks: { stepSize: 1, backdropColor: 'transparent', font: { size: 8 } }
      }
    }
  }
  const attendanceRadarData = {
    labels: ['Late Arrivals', 'Early Departures', 'Missed Clock-Outs'],
    datasets: radarEmployees.map((item, index) => {
      const hue = index * 50
      return {
        label: item.employeeName,
        data: [item.lateDays || 0, item.earlyDepartures || 0, item.missedClockOuts || 0],
        backgroundColor: `hsla(${hue}, 80%, 60%, 0.2)`,
        borderColor: `hsl(${hue}, 80%, 50%)`,
        borderWidth: 2,
        pointBackgroundColor: `hsl(${hue}, 80%, 50%)`,
        fill: true
      }
    })
  }

  const hoursBarOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } }
      },
      tooltip: {
        callbacks: { label: (context) => `${context.dataset.label}: ${context.parsed.x.toFixed(1)}h` }
      }
    },
    scales: {
      x: { stacked: false, grid: { display: false }, ticks: { callback: (val) => val + 'h', font: { size: 10 } } },
      y: { grid: { display: false }, ticks: { font: { size: 10 } } }
    }
  }
  const hoursBarData = {
    labels: hoursRows.map((item) => item.employeeName || 'Unknown'),
    datasets: [
      {
        label: 'Regular Hours',
        data: hoursRows.map((item) => item.totalRegularHours || 0),
        backgroundColor: '#2563EB',
        borderColor: '#1E40AF',
        borderWidth: 1,
        borderRadius: 3
      },
      {
        label: 'OT Hours',
        data: hoursRows.map((item) => item.totalOvertimeHours || 0),
        backgroundColor: '#F97316',
        borderColor: '#EA580C',
        borderWidth: 1,
        borderRadius: 3
      }
    ]
  }
  const hoursBubbleOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || ''
            return [
              `${label}`,
              `Days Present: ${context.parsed.x}d`,
              `Regular Hrs: ${context.parsed.y.toFixed(1)}h`,
              `Avg Hrs/Day: ${(context.raw.r / 3).toFixed(1)}h`
            ]
          }
        }
      }
    },
    scales: {
      x: {
        grid: { color: '#f3f4f6' },
        title: { display: true, text: 'Days Present', color: '#6B7280', font: { size: 11, weight: 'bold' } },
        ticks: { stepSize: 1, callback: (val) => val + 'd', font: { size: 10 } }
      },
      y: {
        grid: { color: '#f3f4f6' },
        title: { display: true, text: 'Regular Hours', color: '#6B7280', font: { size: 11, weight: 'bold' } },
        ticks: { callback: (val) => val + 'h', font: { size: 10 } }
      }
    }
  }
  const hoursBubbleData = {
    datasets: hoursRows.map((item, index) => ({
      label: item.employeeName || 'Unknown',
      data: [{
        x: item.daysPresent || 0,
        y: item.totalRegularHours || 0,
        r: Math.max((item.avgHoursPerDay || 0) * 3, 5)
      }],
      backgroundColor: `hsla(${index * 40}, 70%, 50%, 0.7)`,
      borderColor: `hsla(${index * 40}, 70%, 50%, 1)`,
      borderWidth: 1
    }))
  }

  return (
    <>
      <div className='sm:flex flex-wrap items-center justify-between gap-3 text-center sm:text-left px-3 md:px-4 xl:px-7 lg:px-5 md:py-5 py-4 border bg-white'>
        {!embedded ? (
          <div className='flex flex-wrap items-center'>
            <ODateRangePicker
              handleDateChange={handleDateChange}
              isReset={isReset}
              setIsReset={setIsReset}
              startDate={toPickerDate(startDate)}
              endDate={toPickerDate(endDate)}
            />
            <button
              type='button'
              onClick={handleReset}
              className='bg-LightBlue text-sm px-8 mb-3 ml-3 py-2 rounded-lg items-center border border-transparent text-white hover:bg-DarkBlue sm:w-auto w-1/2 transition duration-200'
            >
              {t('O_RESET')}
            </button>
          </div>
        ) : <div />}
        <div className='flex items-center justify-center sm:justify-end gap-3 mb-3'>
          <span className='text-xs text-slate-500'>
            {t('BI_LAST_UPDATED')}: {lastUpdated ? dayjs(lastUpdated).format('HH:mm:ss') : '—'}
          </span>
          <button
            type='button'
            onClick={() => fetchData(false)}
            className='inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-[#E1E6EE] hover:bg-slate-50'
          >
            <FiRefreshCw className={loading ? 'animate-spin' : ''} />
            {t('BI_REFRESH')}
          </button>
        </div>
      </div>

      <div className='py-4 px-4 md:px-8'>
        <div className='mb-6 bg-gradient-to-r from-[#2B3467] to-[#3E54AC] rounded-lg p-6 text-white shadow-lg'>
          <h2 className='text-2xl font-semibold mt-1'>{t('NAV_BI_DASHBOARD')}</h2>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mt-5'>
            <div>
              <p className='text-xs opacity-80'>{t('BI_TOTAL_EMPLOYEES')}</p>
              <p className='text-xl font-bold'>{hr.evereeSyncedEmployees}</p>
            </div>
            <div>
              <p className='text-xs opacity-80'>Total Overtime Hours</p>
              <p className='text-xl font-bold'>{`${Number(totalRegularHours).toFixed(1)}h`}</p>

            </div>
            <div>
              <p className='text-xs opacity-80'>{t('BI_TOTAL_ASSIGNMENTS')}</p>
              <p className='text-xl font-bold'><CountUp end={staffing.totalAssignments || 0} duration={0.6} /></p>
            </div>
            <div>
              <p className='text-xs opacity-80'>{t('BI_TOTAL_REVENUE')}</p>
              <p className='text-xl font-bold'>${formatMoney(displayRevenue)}</p>
            </div>
          </div>
        </div>

        <div className='flex flex-wrap gap-2 mb-6'>
          {domains.map((domain) => {
            const Icon = domain.icon 
            const active = activeDomain === domain.key
            return (
              <button
                key={domain.key}
                type='button'
                onClick={() => setActiveDomain(domain.key)}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium border transition ${active ? 'bg-LightBlue text-white border-transparent' : 'bg-white text-slate-600 border-[#E1E6EE] hover:bg-slate-50'
                  }`}
              >
                <Icon />
                {domain.label}
              </button>
            )
          })}
        </div>

        {show('hr') && (
          <section className='mb-8'>
            <h3 className='text-lg font-semibold text-DarkBlue mb-4'>{t('BI_HR')}</h3>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-4'>
              <KpiCard title={t('BI_TOTAL_EMPLOYEES')} value={hr.evereeEmployees || hr.totalEmployees} color='green' icon={FiUsers} />
              {/* to={links.employees} */}
              <KpiCard title={t('BI_ACTIVE_EMPLOYEES')} value={hr.activeEmployees} color='blue' icon={FiUserCheck} />
              <KpiCard title={t('BI_EVEREE_SYNCED')} value={hr.evereeSyncedEmployees} color='indigo' icon={FiUserCheck} subtitle={t('BI_EMPLOYEE_SYNC')} />
              <KpiCard title={t('BI_NEW_EMPLOYEES')} value={hr.newEmployees} color='purple' icon={FiUsers} subtitle={t('BI_IN_RANGE')} />
            </div>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
              <ChartCard title={t('BI_BY_DEPARTMENT')}>
                <Bar data={barFromGroups(hr.byDepartment, t('BI_TOTAL_EMPLOYEES'))} options={chartOptions} />
              </ChartCard>
              <ChartCard title={t('BI_BY_LOCATION')}>
                <Doughnut data={doughnutFromGroups(hr.byLocation)} options={pieOptions} />
              </ChartCard>
            </div>
          </section>
        )}

        {show('payroll') && (
          <section className='mb-8'>
            <h3 className='text-lg font-semibold text-DarkBlue mb-4'>{t('BI_PAYROLL')}</h3>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-4'>
              <KpiCard title={t('BI_REGULAR_HOURS')} value={payroll.regularHours} color='indigo' icon={FiClock} />
              <KpiCard title={t('BI_OVERTIME_HOURS')} value={payroll.overtimeHours} color='amber' icon={FiClock} />
              <KpiCard title={t('BI_PAYROLL_RECORDS')} value={payroll.totalRecords} color='purple' icon={FiActivity} />
              <KpiCard title={t('BI_PROCESSING')} value={payroll.processing} color='indigo' icon={FiRefreshCw} />
              <KpiCard title={t('BI_COMPLETED')} value={payroll.completed} color='green' icon={FiUserCheck} />
              <KpiCard title={t('BI_PENDING_TIMESHEETS')} value={payroll.pendingTimesheets} color='purple' icon={FiClock} />
              <KpiCard title={t('BI_EVEREE_AWAITING')} value={payroll.evereeAwaiting} color='amber' icon={FiClock} />
              <KpiCard title={t('BI_EVEREE_SYNCED')} value={payroll.evereeSynced} color='green' icon={FiUserCheck} />
            </div>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
              <ChartCard title={t('BI_PAYROLL_STATUS')}>
                <Doughnut data={doughnutFromGroups(payroll.byStatus)} options={pieOptions} />
              </ChartCard>
              <ChartCard title={t('BI_PAYROLL_HOURS')}>
                <Bar
                  data={{
                    labels: [t('BI_REGULAR_HOURS'), t('BI_OVERTIME_HOURS')],
                    datasets: [{
                      label: t('BI_HOURS'),
                      data: [payroll.regularHours || 0, payroll.overtimeHours || 0],
                      backgroundColor: ['rgba(16, 185, 129, 0.7)', 'rgba(245, 158, 11, 0.7)']
                    }]
                  }}
                  options={chartOptions}
                />
              </ChartCard>
            </div>
          </section>
        )}

        {show('staffing') && (
          <section className='mb-8'>
            <h3 className='text-lg font-semibold text-DarkBlue mb-4'>{t('BI_STAFFING')}</h3>
            <div className='grid grid-cols-2 md:grid-cols-5 gap-4 mb-4'>
              <KpiCard title={t('BI_TOTAL_ASSIGNMENTS')} value={staffing.totalAssignments} color='blue' icon={FiCalendar} desc={"Total number of shifts in the selected period"}/>
              <KpiCard title={t('BI_ACTIVE_ASSIGNMENTS')} value={staffing.activeAssignments} color='indigo' icon={FiCalendar} desc={"Number of shifts with staff assigned"}/>
              <KpiCard title={t('BI_AVAILABLE_STAFF')} value={hr.evereeSyncedEmployees} color='amber' icon={FiUsers} desc={"	Active staff who are not assigned to the matching shifts"}/>
              <KpiCard title={t('BI_PLACED_STAFF')} value={staffing.placedStaff} color='green' icon={FiUserCheck} desc={"Number of unique staff members assigned to shifts"}/>
              <KpiCard title={t('BI_PENDING_STAFFING')} value={staffing.pendingStaffingRequests} color='purple' icon={FiClock} desc={"Number of staffing requests awaiting processing"}/>
            </div>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
              <ChartCard title={t('BI_STAFFING')}>
                <Bar
                  data={{
                    labels: [
                      t('BI_TOTAL_ASSIGNMENTS'),
                      t('BI_ACTIVE_ASSIGNMENTS'),
                      t('BI_AVAILABLE_STAFF'),
                      t('BI_PLACED_STAFF'),
                      t('BI_PENDING_STAFFING')
                    ],
                    datasets: [{
                      label: t('BI_STAFFING'),
                      data: [
                        staffing.totalAssignments || 0,
                        staffing.activeAssignments || 0,
                        hr.evereeSyncedEmployees || 0,
                        staffing.placedStaff || 0,
                        staffing.pendingStaffingRequests || 0
                      ],
                      backgroundColor: [
                        'rgba(62, 84, 172, 0.7)',
                        'rgba(99, 195, 235, 0.7)',
                        'rgba(245, 158, 11, 0.7)',
                        'rgba(16, 185, 129, 0.7)',
                        'rgba(139, 92, 246, 0.7)'
                      ]
                    }]
                  }}
                  options={chartOptions}
                />
              </ChartCard>
            </div>
          </section>
        )}

        {show('revenue') && (
          <section className='mb-8'>
            <h3 className='text-lg font-semibold text-DarkBlue mb-4'>{t('BI_REVENUE')}</h3>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-4'>
              <KpiCard title={t('BI_TOTAL_REVENUE')} value={displayRevenue} color='green' icon={FiTrendingUp} isMoney />
              <KpiCard title={t('BI_COMPLETED_REVENUE')} value={revenue.completed} color='blue' icon={FiDollarSign} isMoney />
              <KpiCard title={t('BI_PENDING_REVENUE')} value={revenue.pending} color='amber' icon={FiClock} isMoney />
              <KpiCard title={t('BI_GROWTH')} value={revenue.growth} suffix='%' color='purple' icon={FiActivity} subtitle={growthLabel} />
            </div>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
              <ChartCard title={t('BI_REVENUE_BY_PERIOD')}>
                <Line
                  data={lineFromTrend(revenue.byPeriod, [
                    { field: 'amount', label: t('BI_TOTAL_REVENUE') },
                    { field: 'completed', label: t('BI_COMPLETED') },
                    { field: 'pending', label: t('BI_PENDING') }
                  ])}
                  options={chartOptions}
                />
              </ChartCard>
              <ChartCard title={t('BI_REVENUE_BY_SERVICE')}>
                <Doughnut data={doughnutFromGroups(revenue.byService, 'totalAmount')} options={pieOptions} />
              </ChartCard>
            </div>
          </section>
        )}

        {show('workforce') && (
          <section className='mb-8'>
            <h3 className='text-lg font-semibold text-DarkBlue mb-4'>{t('BI_WORKFORCE')}</h3>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-6'>
              <KpiCard title={t('BI_TOTAL_WORKFORCE')} value={totalWorkforce} color='blue' icon={FiUsers} />
              <KpiCard title={t('BI_ACTIVE_WORKFORCE')} value={activeWorkforce} color='green' icon={FiUserCheck} />
              <KpiCard title={t('BI_TOTAL_WORKING_HOURS')} value={totalWorkingHours || workforce.totalWorkingHours} color='indigo' icon={FiClock} />
              <KpiCard title={t('BI_OVERTIME_HOURS')} value={totalOTHours || workforce.overtimeHours} color='amber' icon={FiClock} />
            </div>

            <h4 className='text-sm font-semibold text-gray-700 mb-3'>Attendance & Punctuality</h4>
            <div className='grid grid-cols-3 gap-4 mb-4'>
              <ReportKpi label='Total Late Arrivals' value={totalLate} color='orange' />
              <ReportKpi label='Total Absences' value={totalAbsent} color='red' />
              <ReportKpi label='Avg On-Time Rate' value={`${avgOnTimeRate}%`} color='green' />
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-6'>

              {/* // ---------------------------------------------------------------------------------------------------- */}

              <AttendanceOverviewChart rows={attendanceRows} dailyRows={attendanceByDay} />


              {/* ----------------------------------------------------------------------------------------------------------- */}

              <ChartCard title='Punctuality Behavioral Map'>
                {radarEmployees.length ? (
                  <Radar options={attendanceRadarOptions} data={attendanceRadarData} />
                ) : (
                  <div className='flex items-center justify-center h-full text-gray-400 text-sm'>
                    No punctuality data available
                  </div>
                )}
              </ChartCard>
            </div>

            <h4 className='text-sm font-semibold text-gray-700 mb-3'>Employee Hours</h4>
            <div className='grid grid-cols-3 gap-4 mb-4'>
              <ReportKpi label='Total Regular Hours' value={`${Number(totalRegularHours).toFixed(1)}h`} color='blue' />
              <ReportKpi label='Total Overtime Hours' value={`${Number(totalHoursOt).toFixed(1)}h`} color='orange' />
              <ReportKpi label='Avg. Hours / Day' value={`${avgHoursPerDay}h`} color='green' />
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-6'>
              <ChartCard title='Hours Per Employee'>
                {hoursRows.length ? (
                  <Bar options={hoursBarOptions} data={hoursBarData} />
                ) : (
                  <div className='flex items-center justify-center h-full text-gray-400 text-sm'>
                    No employee hours data available
                  </div>
                )}
              </ChartCard>
              <ChartCard title='Workload & Attendance Insights'>
                {hoursRows.length ? (
                  <Bubble options={hoursBubbleOptions} data={hoursBubbleData} />
                ) : (
                  <div className='flex items-center justify-center h-full text-gray-400 text-sm'>
                    No employee hours data available
                  </div>
                )}
              </ChartCard>
            </div>
          </section>
        )}
      </div>
    </>
  )
}

export default BusinessIntelligence